/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Checkbox,
  Flex,
  Grid,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as eva from 'eva-icons'
import { RangeDatepicker } from '../../range'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store/store'
import { setSearchToken, setSelectedDates, setSelectedStatuses } from '../../store/slices/eventSearchSlice'
import { SectionEventTable } from '../EventTable/SectionEventTable'
import { EventStatusFilter, eventStatusFilters, getEventStatusFilterLabelKey } from '../../utils/event-status'

function EventsSearchBar(): JSX.Element {
  const { t } = useTranslation()

  const eventSearch = useSelector((state: RootState) => state.searchEvent)
  const dispatch = useDispatch()

  const handleCheckboxChange = (status: EventStatusFilter): void => {
    const prevSelectedStatuses = eventSearch.selectedStatuses
    dispatch(setSelectedStatuses(prevSelectedStatuses.includes(status) ? prevSelectedStatuses.filter((s) => s !== status) : [...prevSelectedStatuses, status]))
  }

  const removeStatus = (): void => {
    dispatch(setSelectedStatuses([]))
  }

  const removeSelectedDates = (): void => {
    dispatch(setSelectedDates([]))
  }

  useEffect(() => {
    eva.replace()
  }, [])

  const dateFilterString = useMemo((): string => {
    const { selectedDates } = eventSearch
    if (selectedDates.length <= 1) {
      return ''
    }
    const firstDay = selectedDates[0]
    const lastDay = selectedDates[1]
    const lastPart = `${selectedDates[1].getDate()} ${selectedDates[1].toLocaleString('it-IT', { month: 'short' })} ${selectedDates[1].getFullYear()}`
    if (firstDay.getFullYear() !== lastDay.getFullYear() || firstDay.getMonth() !== lastDay.getMonth()) {
      const firstPart = `${selectedDates[0].getDate()} ${selectedDates[0].toLocaleString('it-IT', { month: 'short' })} ${selectedDates[0].getFullYear()}`
      return `${firstPart} - ${lastPart}`
    } else {
      return `${selectedDates[0].getDate()} - ${lastPart}`
    }
  }, [eventSearch])

  const searchInput = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatch(
        setSearchToken({
          token: e.target.value,
          pageSize: eventSearch.pageSize,
          currentPage: 1,
          selectedDates: eventSearch.selectedDates,
          selectedStatuses: eventSearch.selectedStatuses
        })
      )
    },
    [dispatch, eventSearch.pageSize, eventSearch.selectedDates, eventSearch.selectedStatuses]
  )

  return (
    <Flex flexDirection={'column'} width={'100%'} height={'100vh'}>
      <HStack spacing={4} mb={4} height={'52px'}>
        <InputGroup>
          <InputLeftElement pointerEvents='none'>
            <i data-eva={'search-outline'}></i>
          </InputLeftElement>
          <Input
            autoCapitalize='sentences'
            placeholder={t('search_event_placeholder')}
            bg={'NeutralLight'}
            borderWidth='1px'
            borderColor='greyCold200'
            borderStyle='solid'
            border={'1px solid neutralDark'}
            borderRadius={'16px'}
            gap={'16px'}
            value={eventSearch.token ?? ''}
            onChange={searchInput}
          />
        </InputGroup>
      </HStack>
      <HStack>
        {eventSearch.selectedStatuses.length > 0 ? (
          <HStack>
            <Text
              bg={'primary.400'}
              border={'1px solid primary.400'}
              borderRadius='600px'
              alignItems={'center'}
              padding={'8px 10px'}
              gap='2px'
              color={'NeutralLight'}
              fontWeight='600'
              fontSize='14px'
              lineHeight='19.6px'>
              {t(getEventStatusFilterLabelKey(eventSearch.selectedStatuses[0]))}{' '}
              {eventSearch.selectedStatuses.length > 1 ? `(+${eventSearch.selectedStatuses.length - 1})` : ''}
              <Button alignItems={'center'} size='xs' onClick={(): void => removeStatus()}>
                <Text
                  bg={'primary.400'}
                  border={'1px solid primary.400'}
                  borderRadius='600px'
                  alignItems={'center'}
                  padding={'2px 0px'}
                  color={'NeutralLight'}
                  fontWeight='600'
                  fontSize='14px'
                  lineHeight='19.6px'>
                  X
                </Text>
              </Button>
            </Text>
          </HStack>
        ) : (
          <div />
        )}
        {eventSearch.selectedDates.length > 1 ? (
          <Text
            bg={'primary.400'}
            border={'1px solid primary.400'}
            borderRadius='600px'
            alignItems={'center'}
            padding={'8px 10px'}
            gap='2px'
            color={'NeutralLight'}
            fontWeight='600'
            fontSize='14px'
            lineHeight='19.6px'>
            {dateFilterString}
            <Button alignItems={'center'} size='xs' onClick={(): void => removeSelectedDates()}>
              <Text
                bg={'primary.400'}
                border={'1px solid primary.400'}
                borderRadius='600px'
                alignItems={'center'}
                padding={'2px 0px'}
                color={'NeutralLight'}
                fontWeight='600'
                fontSize='14px'
                lineHeight='19.6px'>
                X
              </Text>
            </Button>
          </Text>
        ) : (
          <div />
        )}
        <Popover placement='bottom-start'>
          <PopoverTrigger>
            <Button
              borderRadius='600px'
              padding={'8px, 10px, 8px, 10px'}
              gap='2px'
              bg='greyWarm100'
              border={'5px solid primary.400'}
              fontWeight='600'
              fontSize='14px'
              lineHeight='19.6px'
              textTransform='capitalize'>
              {t('status')}
              <i data-eva={'chevron-down'}></i>
            </Button>
          </PopoverTrigger>
          <PopoverContent borderRadius={'26px'}>
            <PopoverBody display={'flex'} flexDirection='column' gap={'26px'} padding={'20px'}>
              {eventStatusFilters.map((s) => (
                <Checkbox
                  key={s}
                  variant={'primary'}
                  value={s}
                  isChecked={eventSearch.selectedStatuses.includes(s)}
                  onChange={(): void => handleCheckboxChange(s)}>
                  {t(getEventStatusFilterLabelKey(s))}
                </Checkbox>
              ))}
            </PopoverBody>
          </PopoverContent>
        </Popover>
        <RangeDatepicker
          buttonText='event_date_search'
          selectedDates={eventSearch.selectedDates}
          onDateChange={(dates: Date[]): void => {
            dispatch(setSelectedDates(dates))
          }}></RangeDatepicker>
      </HStack>
      <Grid flexDirection='column' overflow={'auto'} height={'80%'} mt={4}>
        <SectionEventTable />
      </Grid>
    </Flex>
  )
}

export { EventsSearchBar }
